import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightAndArrowDownLeftFromCenter,
  faGripDots,
  faPencil,
  faTrashCan,
} from '@fortawesome/pro-solid-svg-icons';
import {
  CELSIUS_ID,
  FAHRENHEIT_ID,
  SPEED_FEET,
  SPEED_METERS,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import WelcomeWidgetEdit from './welcomewidget/WelcomeWidgetEdit';
import './DashboardWidget.css';
import WelcomeWidgetView from './welcomewidget/WelcomeWidgetView';
import WidgetSkeleton from './WidgetSkeleton';
import WidgetTypePicker from './WidgetTypePicker';
import CommonWidgetEditFields from './CommonWidgetEditFields';
import HeatIndexGaugeWidgetView, { useHeatIndexGaugeWidgetData } from './heatindexwidget/HeatIndexGaugeWidgetView';
import HeatIndexGaugeWidgetEdit from './heatindexwidget/HeatIndexGaugeWidgetEdit';
import AlertsListWidgetView from './alertlistwidget/AlertsListWidgetView';
import AlertsListWidgetEdit, { ALERTS_LIST_DEFAULT_CONTEXT } from './alertlistwidget/AlertsListWidgetEdit';
import { useAlertsListWidgetData } from './alertlistwidget/useAlertsListWidgetData';
import AlertHistoryWidgetEdit from './alerthistorywidget/AlertHistoryWidgetEdit';
import DeviceMapWidgetView, { useDeviceMapWidgetData } from './devicemapwidget/DeviceMapWidgetView';
import DeviceMapWidgetEdit from './devicemapwidget/DeviceMapWidgetEdit';
import { SENSOR_DATA_DEFAULT_CONTEXT } from './sensordatawidget/sensorDataWidgetTypes';
import { useSensorDataWidgetData } from './sensordatawidget/useSensorDataWidgetData';
import SensorDataWidgetEdit from './sensordatawidget/SensorDataWidgetEdit';
import SensorDataWidgetView from './sensordatawidget/SensorDataWidgetView';
import AlertsListWidgetDefault from './alertlistwidget/AlertListWidgetDefault';
import HeatIndexGaugeWidgetDefault from './heatindexwidget/HeatIndexGaugeWidgetDefault';
import SensorDataWidgetDefault from './sensordatawidget/SensorDataWidgetDefault';
import AlertHistoryWidgetView, { useAlertHistoryWidgetData } from './alerthistorywidget/AlertHistoryWidgetView';
import StatisticsWidgetEdit, { STATISTICS_DEFAULT_CONTEXT } from './statisticswidget/StatisticsWidgetEdit';
import StatisticsWidgetView, { useStatisticsWidgetData } from './statisticswidget/StatisticsWidgetView';
import { ALERT_HISTORY_DEFAULT_CONTEXT } from './alerthistorywidget/alertHistoryDefaultContext';
import ChannelWidgetView, { useChannelWidgetData } from './channelwidget/ChannelWidgetView';
import ChannelWidgetEdit, { CHANNEL_WIDGET_DEFAULT_CONTEXT } from './channelwidget/ChannelWidgetEdit';

const TILE_ICON = faGripDots;
const DANGER = '#dc3545';
const ERROR_BORDER_STYLE = {
  border: `2px solid ${DANGER}`,
  borderRadius: '0.25rem',
  boxShadow: `0 0 0 0.2rem ${'rgba(220, 53, 69, 0.25)'}`,
};
const WIDGETS = {
  1: {
    title: 'Welcome',
    View: WelcomeWidgetView,
    Edit: WelcomeWidgetEdit,
    allowedSizes: [{ sizeX: 6, sizeY: 3 }],
  },
  7: {
    title: 'Heat Index Gauge',
    View: HeatIndexGaugeWidgetView,
    Edit: HeatIndexGaugeWidgetEdit,
    Default: HeatIndexGaugeWidgetDefault,
    useData: useHeatIndexGaugeWidgetData,
    allowedSizes: [
      { sizeX: 2, sizeY: 2 },
      { sizeX: 3, sizeY: 3 },
      { sizeX: 4, sizeY: 4 },
    ],
  },
  3: {
    title: 'Alert History Table',
    View: AlertHistoryWidgetView,
    Edit: AlertHistoryWidgetEdit,
    useData: useAlertHistoryWidgetData,
    defaultContext: ALERT_HISTORY_DEFAULT_CONTEXT,
    allowedSizes: [
      { sizeX: 6, sizeY: 3 },
      { sizeX: 6, sizeY: 4 },
      { sizeX: 8, sizeY: 4 },
      { sizeX: 8, sizeY: 4 },
      { sizeX: 12, sizeY: 4 },
      { sizeX: 12, sizeY: 6 },
      { sizeX: 3, sizeY: 6 },
      { sizeX: 4, sizeY: 8 },
      { sizeX: 4, sizeY: 10 },
      { sizeX: 6, sizeY: 12 },
      { sizeX: 4, sizeY: 4 },
      { sizeX: 6, sizeY: 6 },
      { sizeX: 8, sizeY: 8 },
      { sizeX: 12, sizeY: 12 },
    ],
  },
  5: {
    title: 'Device Map',
    View: DeviceMapWidgetView,
    Edit: DeviceMapWidgetEdit,
    useData: useDeviceMapWidgetData,
    allowedSizes: [
      { sizeX: 4, sizeY: 2 },
      { sizeX: 6, sizeY: 3 },
      { sizeX: 6, sizeY: 4 },
      { sizeX: 8, sizeY: 4 },
      { sizeX: 12, sizeY: 6 },
    ],
  },
  2: {
    title: 'Alerts List',
    View: AlertsListWidgetView,
    Edit: AlertsListWidgetEdit,
    Default: AlertsListWidgetDefault,
    useData: useAlertsListWidgetData,
    defaultContext: ALERTS_LIST_DEFAULT_CONTEXT,
    allowedSizes: [
      { sizeX: 6, sizeY: 3 },
      { sizeX: 6, sizeY: 4 },
      { sizeX: 8, sizeY: 4 },
      { sizeX: 12, sizeY: 4 },
      { sizeX: 12, sizeY: 6 },
      { sizeX: 3, sizeY: 6 },
      { sizeX: 4, sizeY: 8 },
      { sizeX: 4, sizeY: 10 },
      { sizeX: 6, sizeY: 12 },
      { sizeX: 4, sizeY: 4 },
      { sizeX: 6, sizeY: 6 },
      { sizeX: 8, sizeY: 8 },
      { sizeX: 12, sizeY: 12 },
    ],
  },
  4: {
    title: 'Sensor Data',
    View: SensorDataWidgetView,
    Edit: SensorDataWidgetEdit,
    Default: SensorDataWidgetDefault,
    useData: useSensorDataWidgetData,
    defaultContext: SENSOR_DATA_DEFAULT_CONTEXT,
    allowedSizes: [
      { sizeX: 6, sizeY: 4 },
      { sizeX: 8, sizeY: 4 },
      { sizeX: 8, sizeY: 6 },
      { sizeX: 12, sizeY: 4 },
      { sizeX: 12, sizeY: 6 },
      { sizeX: 12, sizeY: 8 },
      { sizeX: 6, sizeY: 6 },
      { sizeX: 6, sizeY: 5 },
      { sizeX: 8, sizeY: 8 },
      { sizeX: 12, sizeY: 12 },
    ],
  },
  6: {
    title: 'Statistics',
    View: StatisticsWidgetView,
    Edit: StatisticsWidgetEdit,
    useData: useStatisticsWidgetData,
    defaultContext: STATISTICS_DEFAULT_CONTEXT,
    allowedSizes: [
      { sizeX: 3, sizeY: 3 },
      { sizeX: 4, sizeY: 2 },
      { sizeX: 6, sizeY: 3 },
      { sizeX: 8, sizeY: 4 },
      { sizeX: 4, sizeY: 4 },
      { sizeX: 6, sizeY: 6 },
    ],
  },
  8: {
    title: 'Individual Reading',
    View: ChannelWidgetView,
    Edit: ChannelWidgetEdit,
    useData: useChannelWidgetData,
    defaultContext: CHANNEL_WIDGET_DEFAULT_CONTEXT,
    allowedSizes: [
      { sizeX: 1, sizeY: 1 },
      { sizeX: 2, sizeY: 1 },
    ],
  },
};

export const getWidgetDefinition = (widgetTypeId) => (
  widgetTypeId != null ? WIDGETS[widgetTypeId] : null
);

export const getAllowedSizes = (widgetTypeId) => {
  const def = getWidgetDefinition(widgetTypeId);
  return (def && def.allowedSizes) || null;
};

export const getDefaultSizeForType = (widgetTypeId) => {
  const def = getWidgetDefinition(widgetTypeId);
  if (def && def.defaultSize) return def.defaultSize;
  if (def && def.allowedSizes && def.allowedSizes.length > 0) return def.allowedSizes[0];
  return null;
};

export const getDefaultContextForType = (widgetTypeId) => {
  const def = getWidgetDefinition(widgetTypeId);
  return (def && def.defaultContext) || null;
};

const useNoData = () => ({
  isLoading: false, data: null, viewState: {}, isDefault: false,
});

const DashboardWidget = ({
  widgetTypeId,
  widgetId,
  context,
  isEditMode,
  isViewOnly,
  isResizable,
  sizeX,
  sizeY,
  refreshTick,
  onDelete,
  onContextChange,
  onTypeChange,
  onRequestEditMode,
  userPreferences,
  errors,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const definition = widgetTypeId != null ? WIDGETS[widgetTypeId] : null;
  const isUntyped = widgetTypeId == null;
  const title = definition ? definition.title : 'Select Tile Type';
  const hasEdit = !!(definition && definition.Edit);
  const showEdit = !isViewOnly && isEditMode && hasEdit;
  const showChrome = !isViewOnly && isEditMode;

  // Small widgets (2x2 or below) don't have room for the inline edit form,
  // so we show a pencil button that opens the same form in a modal instead.
  const isSmall = sizeX != null && sizeY != null && sizeX <= 2 && sizeY <= 2;
  const useModalEdit = showEdit && isSmall;

  const ctx = context || {};
  const showContextTitle = !showChrome && !!ctx.showTitle && !!ctx.title;
  const useData = (definition && definition.useData) || useNoData;
  const {
    isLoading, data, viewState, isDefault,
  } = useData(ctx, {
    isEditMode: showEdit,
    widgetId,
    userPreferences,
    refreshTick,
  });

  const showDefault = !!isDefault && !showEdit && !!(definition && definition.Default);
  const widgetLevelError = (errors || []).find((e) => !e.field);

  const renderEditForm = () => {
    const EditComponent = definition.Edit;
    return (
      <>
        <CommonWidgetEditFields
          context={context || {}}
          onChange={onContextChange}
          errors={errors}
        />
        <EditComponent
          context={context || {}}
          onChange={onContextChange}
          errors={errors}
        />
      </>
    );
  };

  let body;
  if (isUntyped && isEditMode && !isViewOnly) {
    body = (
      <>
        <WidgetTypePicker
          options={Object.entries(WIDGETS).map(([key, def]) => ({
            value: Number(key),
            label: def.title,
          }))}
          onSelect={onTypeChange}
        />
        {widgetLevelError && (
          <div className="text-danger small text-center mt-2 px-2" role="alert">
            {widgetLevelError.message}
          </div>
        )}
      </>
    );
  } else if (!definition) {
    body = <div className="widget__fallback">No component registered</div>;
  } else if (showEdit && !useModalEdit) {
    // Errors are handed to each child directly rather than via a wrapping
    // FormErrorHandler — FormErrorHandler.recursiveMapChildren only sees
    // JSX in its immediate subtree, not inside custom child components, so
    // wrapping here wouldn't reach ErrorWrapper elements defined inside
    // CommonWidgetEditFields or the widget's own Edit form.
    body = (
      <div className="widget__edit-form mx-auto">
        {renderEditForm()}
      </div>
    );
  } else if (useModalEdit) {
    // Small tile: show the edit + delete buttons centered in the body
    // instead of tucked into the corner chrome.
    body = (
      <div className="widget__small-edit-actions">
        <button
          type="button"
          onClick={() => setIsEditModalOpen(true)}
          className="btn btn-outline-secondary rounded-circle widget__center-button"
          aria-label="Edit widget"
        >
          <FontAwesomeIcon icon={faPencil} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="btn btn-outline-secondary rounded-circle widget__center-button"
          aria-label="Delete widget"
        >
          <FontAwesomeIcon icon={faTrashCan} />
        </button>
      </div>
    );
  } else if (isLoading && data == null) {
    body = <WidgetSkeleton {...(definition.skeleton || {})} />;
  } else if (showDefault) {
    const DefaultComponent = definition.Default;
    body = (
      <div className="w-100 h-100" style={{ minHeight: 0 }}>
        <DefaultComponent onRequestEdit={onRequestEditMode} />
      </div>
    );
  } else {
    const ViewComponent = definition.View;
    body = (
      <div className="w-100 h-100" style={{ minHeight: 0 }}>
        <ViewComponent
          context={context || {}}
          data={data}
          viewState={viewState || {}}
          onContextChange={onContextChange}
          userPreferences={userPreferences}
          widgetId={widgetId}
          sizeX={sizeX}
          sizeY={sizeY}
          onRequestEditMode={onRequestEditMode}
        />
      </div>
    );
  }

  const hasErrors = Array.isArray(errors) && errors.length > 0;
  const widgetClassName = [
    'widget',
    showChrome ? 'widget--edit' : '',
    hasErrors ? 'widget--error' : '',
  ].filter(Boolean).join(' ');
  const widgetStyle = hasErrors ? ERROR_BORDER_STYLE : undefined;

  return (
    <div className={widgetClassName} style={widgetStyle} aria-invalid={hasErrors || undefined}>
      {showChrome && (
        <div className="widget__drag-handle">
          <span className={`widget__title${!useModalEdit ? ' widget__title--with-delete' : ''}`}>
            <FontAwesomeIcon
              icon={TILE_ICON}
              className="widget__title-icon text-muted mr-1"
              size="2x"
            />
            <span className="widget__title-text">{title}</span>
          </span>
          {!useModalEdit && (
            <button
              type="button"
              onClick={onDelete}
              className="btn btn-outline-secondary rounded-circle widget__delete-button"
              aria-label="Delete widget"
            >
              <FontAwesomeIcon icon={faTrashCan} />
            </button>
          )}
          {sizeX != null && sizeY != null && (
            <span className="widget__size-badge" aria-hidden="true">
              {`${sizeX}×${sizeY}`}
            </span>
          )}
          {isResizable && (
            <span className="widget__resize-handle text-secondary" aria-hidden="true">
              <FontAwesomeIcon icon={faArrowUpRightAndArrowDownLeftFromCenter} flip="horizontal" size="lg" />
            </span>
          )}
        </div>
      )}
      {showContextTitle && (
        <span className="widget__title">
          {ctx.title}
        </span>
      )}
      <div className="widget__content">{body}</div>
      {useModalEdit && (
        <Modal
          show={isEditModalOpen}
          onHide={() => setIsEditModalOpen(false)}
          size="lg"
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={TILE_ICON} className="widget__title-icon text-muted mr-1" size="2x" />
              <span className="widget__title-text">{title}</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="widget__edit-form">
              {renderEditForm()}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setIsEditModalOpen(false)}
            >
              Done
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

DashboardWidget.propTypes = {
  widgetTypeId: PropTypes.number,
  widgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // eslint-disable-next-line react/forbid-prop-types
  context: PropTypes.object,
  isEditMode: PropTypes.bool,
  isViewOnly: PropTypes.bool,
  isResizable: PropTypes.bool,
  sizeX: PropTypes.number,
  sizeY: PropTypes.number,
  onToggleEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onContextChange: PropTypes.func,
  onTypeChange: PropTypes.func,
  refreshTick: PropTypes.number,
  onRequestEditMode: PropTypes.func,
  userPreferences: PropTypes.shape({
    temperatureScale: PropTypes.oneOf([CELSIUS_ID, FAHRENHEIT_ID]),
    airSpeedScale: PropTypes.oneOf([SPEED_METERS, SPEED_FEET]),
    timezone: PropTypes.string,
  }),
  errors: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string,
    message: PropTypes.string,
    code: PropTypes.string,
  })),
};

DashboardWidget.defaultProps = {
  widgetTypeId: null,
  widgetId: null,
  context: {},
  isEditMode: false,
  isViewOnly: false,
  isResizable: true,
  sizeX: null,
  sizeY: null,
  refreshTick: 0,
  onToggleEdit: () => {},
  onDelete: () => {},
  onContextChange: () => {},
  onTypeChange: () => {},
  onRequestEditMode: () => {},
  userPreferences: {
    temperatureScale: CELSIUS_ID,
    airSpeedScale: SPEED_METERS,
  },
  errors: [],
};

export default DashboardWidget;
